import type { PipelineEvent } from "../types/pipeline-event";
import { resolvePath } from "./path";
import type { FilterCondition, FilterOp, PipelineFilter } from "./types";

export interface FilterResult {
  skip: boolean;
  reason?: string;
}

/**
 * Evaluate a pipeline filter DSL against an event.
 *
 * Empty / absent filter (or a filter with no conditions) is a no-op → `{ skip: false }`,
 * so `{}` remains "pass everything" for back-compat. Otherwise each condition is
 * evaluated to a boolean, combined via `match` (all=AND, any=OR), then `action`
 * decides the outcome: `exclude` skips when the combined result is true, `include`
 * (allow-list) skips when it's false.
 */
export function evaluateFilter(
  filter: PipelineFilter | Record<string, unknown> | null | undefined,
  // The object field paths resolve against — the parsed event (pre-transform)
  // or the transform output (post-transform). resolvePath treats it as an opaque
  // root, so any plain object works.
  target: PipelineEvent | Record<string, unknown>,
): FilterResult {
  if (!isFilterDsl(filter)) return { skip: false };
  const { action, match, conditions } = filter;
  if (!Array.isArray(conditions) || conditions.length === 0) return { skip: false };

  const results = conditions.map((c) => evaluateCondition(c, target));
  const matchedCount = results.filter(Boolean).length;
  const combined = match === "all" ? matchedCount === conditions.length : matchedCount > 0;

  const skip = action === "exclude" ? combined : !combined;
  if (!skip) return { skip: false };

  return { skip: true, reason: describeReason(action, match, conditions, matchedCount) };
}

function isFilterDsl(
  filter: PipelineFilter | Record<string, unknown> | null | undefined,
): filter is PipelineFilter {
  return (
    filter != null &&
    typeof filter === "object" &&
    "action" in filter &&
    "match" in filter &&
    "conditions" in filter
  );
}

/**
 * Which object a filter's field paths resolve against: `"output"` (the transform
 * result) or `"event"` (the incoming event, the default). Callers use this to
 * pick the evaluation point — pre-transform for `event`, post-transform for
 * `output`. A non-DSL/empty filter reports `"event"` (it's a no-op anyway).
 */
export function filterSource(
  filter: PipelineFilter | Record<string, unknown> | null | undefined,
): "event" | "output" {
  return isFilterDsl(filter) && filter.source === "output" ? "output" : "event";
}

function evaluateCondition(condition: FilterCondition, target: PipelineEvent | Record<string, unknown>): boolean {
  const { field, op, value, caseInsensitive } = condition;
  const values = resolvePath(target, field);

  if (op === "exists") {
    return values.some((v) => v !== null && v !== undefined);
  }

  if (op === "notEquals") {
    // True unless some resolved value equals the target. Missing path → nothing
    // equals → true.
    return !values.some((v) => scalarEquals(v, value, caseInsensitive));
  }

  if (op === "in") {
    const list = Array.isArray(value) ? value : [];
    return values.some((v) => list.some((item) => scalarEquals(v, item, caseInsensitive)));
  }

  // Remaining ops all require at least one resolved value to be true.
  return values.some((v) => matchScalar(op, v, value, caseInsensitive));
}

function matchScalar(
  op: FilterOp,
  resolved: unknown,
  value: FilterCondition["value"],
  caseInsensitive?: boolean,
): boolean {
  switch (op) {
    case "equals":
      return scalarEquals(resolved, value, caseInsensitive);
    case "contains":
      return withCase(str(resolved), caseInsensitive).includes(withCase(str(value), caseInsensitive));
    case "startsWith":
      return withCase(str(resolved), caseInsensitive).startsWith(withCase(str(value), caseInsensitive));
    case "endsWith":
      return withCase(str(resolved), caseInsensitive).endsWith(withCase(str(value), caseInsensitive));
    case "matchesRegex":
      try {
        const re = new RegExp(str(value), caseInsensitive ? "i" : "");
        return re.test(str(resolved));
      } catch {
        return false;
      }
    default:
      return false;
  }
}

function scalarEquals(a: unknown, b: FilterCondition["value"], caseInsensitive?: boolean): boolean {
  if (typeof a === "number" || typeof a === "boolean" || typeof b === "number" || typeof b === "boolean") {
    // Number/boolean comparisons are by string-coerced value so `qty: 3` matches
    // both `3` and `"3"`. (Case folding is irrelevant for these.)
    return str(a) === str(b);
  }
  return withCase(str(a), caseInsensitive) === withCase(str(b), caseInsensitive);
}

function str(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v);
}

function withCase(s: string, caseInsensitive?: boolean): string {
  return caseInsensitive ? s.toLowerCase() : s;
}

function describeReason(
  action: PipelineFilter["action"],
  match: PipelineFilter["match"],
  conditions: FilterCondition[],
  matchedCount: number,
): string {
  // describeReason only runs when the event is being SKIPPED. Descriptive
  // single-condition form applies to `exclude`, where the one matched condition
  // drove the skip.
  if (action === "exclude" && conditions.length === 1) {
    const c = conditions[0];
    if (c.op === "exists") return `excluded: ${c.field} exists`;
    return `excluded: ${c.field} ${c.op} "${formatValue(c.value)}"`;
  }
  if (action === "exclude") {
    return `excluded: matched ${matchedCount} of ${conditions.length} conditions (${match})`;
  }
  // `include` (allow-list): a skip means the event FAILED the inclusion criteria,
  // so it's being excluded from delivery — never say "included" for a dropped event.
  return `excluded (did not match include filter): matched ${matchedCount} of ${conditions.length} conditions (${match})`;
}

function formatValue(value: FilterCondition["value"]): string {
  return Array.isArray(value) ? value.join(", ") : str(value);
}
